import React, { useState, useEffect } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";

import Sidebar from "./TabletSidebar";
import Navbar from "./TabletNavbarComponets/TabletNavbar";

const Layout = ({ children }) => {
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";
  const theme = useTheme();

  // ✅ Width-based breakpoints only
  const isSmallTablet = useMediaQuery("(min-width: 600px) and (max-width: 768px)");
  const isMediumTablet = useMediaQuery("(min-width: 769px) and (max-width: 900px)");
  const isLargeTablet = useMediaQuery("(min-width: 901px) and (max-width: 1024px)");
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 1024px)");

  // ✅ Orientation only (no height checks)
  const isPortrait = useMediaQuery("(orientation: portrait)");
  const isLandscape = useMediaQuery("(orientation: landscape)");

  // ✅ Sidebar width calculation
  const getSidebarWidth = () => {
    if (!isSidebarOpen) return 70;

    if (isSmallTablet) {
      return isPortrait ? 240 : 200;
    } else if (isMediumTablet) {
      return isPortrait ? 260 : 220;
    } else if (isLargeTablet) {
      return isPortrait ? 280 : 240;
    }
    return 280;
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const sidebarWidth = getSidebarWidth();

  // ✅ Auto-collapse sidebar on small portrait
  useEffect(() => {
    if (isSmallTablet && isPortrait) {
      setIsSidebarOpen(false);
    } else if (isLandscape) {
      setIsSidebarOpen(true);
    }
  }, [isSmallTablet, isPortrait, isLandscape]);

  // ✅ Return children without layout if not tablet
  if (!isTablet) {
    return children;
  }

  // ✅ Content padding based on screen size
  const getContentPadding = () => {
    if (isSmallTablet) return 1.5;
    if (isMediumTablet) return 2;
    return 2.5;
  };

  return (
    <Box
      sx={{
        // ✅ ROOT: Flexbox fills viewport without overflow issues
        display: "flex",
        direction: "rtl",
        width: "100%",
        height: "100dvh", // Dynamic viewport height
        minHeight: 0, // Critical for flex children
        overflow: "hidden", // Prevent body scroll
        bgcolor: theme.palette.mode === "dark" ? "#0f0f1e" : "#f5f5f5",
        position: "relative",
      }}
    >
      {/* ✅ SIDEBAR: Fixed width, no flex-grow */}
      {!isLoginPage && (
        <Box
          sx={{
            width: sidebarWidth,
            flexShrink: 0, // Don't shrink
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            bgcolor: theme.palette.mode === "dark" ? "#1F1D2B" : "#fff",
            boxShadow: "2px 0 12px rgba(0,0,0,0.08)",
            zIndex: 1100,
            overflow: "hidden", // Sidebar has its own scroll
          }}
        >
          <Sidebar
            isOpen={isSidebarOpen}
            toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          />
        </Box>
      )}

      {/* ✅ MAIN CONTENT: Flex-grow fills remaining space */}
      <Box
        component="main"
        sx={{
          flex: 1, // Fills remaining horizontal space
          minWidth: 0, // Prevent overflow
          display: "flex",
          flexDirection: "column",
          minHeight: 0, // Critical for vertical flex children
          overflow: "hidden", // Content area controls scroll
        }}
      >
        {/* ✅ NAVBAR: Fixed height, no flex-grow */}
        {!isLoginPage && (
          <Box
            sx={{
              flexShrink: 0, // Don't shrink
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(30, 30, 30, 0.95)"
                  : "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(8px)",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              zIndex: 1099,
              minHeight: isSmallTablet ? 56 : isMediumTablet ? 60 : 64,
            }}
          >
            <Navbar
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
            />
          </Box>
        )}

        {/* ✅ CONTENT AREA: Single scroll container */}
        <Box
          sx={{
            flex: 1, // Fills remaining vertical space
            minHeight: 0, // Critical for scroll to work
            overflowY: "auto",
            overflowX: "hidden",
            padding: getContentPadding(),
            scrollBehavior: "smooth",
            WebkitOverflowScrolling: "touch",
            // ✅ Custom scrollbar
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark" ? "#444 #1a1a1a" : "#ccc #f5f5f5",
            "&::-webkit-scrollbar": {
              width: isSmallTablet ? "6px" : "8px",
            },
            "&::-webkit-scrollbar-track": {
              bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: theme.palette.mode === "dark" ? "#444" : "#ccc",
              borderRadius: "4px",
              "&:hover": {
                bgcolor: theme.palette.mode === "dark" ? "#555" : "#bbb",
              },
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
